import _ from 'lodash';
import { useState, useCallback } from 'react';
import {
  type RowGroupOpenedEvent,
  type IRowNode,
  type IsServerSideGroupOpenByDefaultParams,
} from 'ag-grid-community';
import { isTablePrintingImprovementInheritGroupExpansionEnabled } from 'common/visualizations/helpers/VifSelectors';

/**
 * Generates a unique identifier for a group node based on its route path.
 * This matches the format used by AG-Grid's internal grouping system.
 */
const groupNodeId = (node: IRowNode): string => {
  const route = node.getRoute();
  return _.isEmpty(route) ? '' : JSON.stringify(route);
};

export const useGroupStateRestore = () => {
  const [expandedRoutesMap] = useState<Record<string, boolean>>({});
  const [manualGroupStatesMap, setManualGroupStatesMap] = useState<Record<string, boolean>>({});

  const isServerSideGroupOpenByDefault = useCallback(
    (params: IsServerSideGroupOpenByDefaultParams) => {
      if (isTablePrintingImprovementInheritGroupExpansionEnabled()) {
        const groupKey = groupNodeId(params.rowNode);
        // Only return true if explicitly set to true (manually expanded)
        // false or undefined both mean "not manually expanded"
        return manualGroupStatesMap[groupKey] === true;
      } else {
        return groupNodeId(params.rowNode) in expandedRoutesMap;
      }
    },
    [manualGroupStatesMap, expandedRoutesMap],
  );

  const onRowGroupOpened = useCallback(
    ({ node }: RowGroupOpenedEvent) => {
      const groupKey = groupNodeId(node);

      if (isTablePrintingImprovementInheritGroupExpansionEnabled()) {
        setManualGroupStatesMap((previousStates) => ({
          ...previousStates,
          // Store both expand AND collapse actions to preserve manual user intent
          [groupKey]: node.expanded,
        }));
      } else {
        if (node.expanded) {
          expandedRoutesMap[groupKey] = true;
        } else {
          delete expandedRoutesMap[groupKey];
        }
      }
    },
    [expandedRoutesMap],
  );

  const getExpansionState = useCallback(() => {
    if (isTablePrintingImprovementInheritGroupExpansionEnabled()) {
      return manualGroupStatesMap;
    }
    return {};
  }, [manualGroupStatesMap]);

  return { isServerSideGroupOpenByDefault, onRowGroupOpened, getExpansionState };
};
